import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { fetchContacts } from '@modules-deprecated/app/contacts/query-api'
import type { FetchContactsQueryParams } from '@modules-deprecated/app/contacts/query-api'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../enums/queryKeys'
import { getMatchedSupplierContactId } from '../utils/getMatchedSupplierContactId'

interface UseMatchedSupplierContactIdProps {
  supplierName?: string
}

interface UseMatchedSupplierContactIdResult {
  isLoading: boolean
  supplierId: string | null
}

export const useMatchedSupplierContactId = ({
  supplierName,
}: UseMatchedSupplierContactIdProps): UseMatchedSupplierContactIdResult => {
  const { organization } = useUserOrganization()
  const organizationId = organization?.id
  const trimmedSupplierName = supplierName?.trim()

  const queryProps: FetchContactsQueryParams | undefined = useMemo(() => {
    if (!organizationId || !trimmedSupplierName) {
      return undefined
    }

    return {
      isSupplier: true,
      organizationId,
      page: 1,
      pageSize: 100,
      q: trimmedSupplierName,
    }
  }, [organizationId, trimmedSupplierName])

  const { data: contactsData, isLoading } = useQuery(
    [QueryKeys.Contacts, queryProps],
    () => fetchContacts(queryProps as FetchContactsQueryParams),
    {
      enabled: queryProps !== undefined,
    },
  )

  const supplierId = useMemo(
    () => getMatchedSupplierContactId({ contacts: contactsData?.contacts, supplierName: trimmedSupplierName }),
    [contactsData?.contacts, trimmedSupplierName],
  )

  return useMemo(() => ({ isLoading, supplierId }), [isLoading, supplierId])
}
